import {isUndefined} from 'lodash';
import {useMemo} from 'react';

import {collectEnumValues} from '../../../../libs/shared/helpers/src/typescript/enumHelpers';
import {SettingsCustomFieldsPagesEnum} from '../../../models/url/settingsUrlModel';
import {FeatureFlagsMap} from '../../../store/featureFlags/featureFlagsState';
import {ClientAtom, isInFunctionAtom} from './expressionAtomModel';
import {useExpressionsFeatureFlags} from './expressionFeatureFlags';
import {
  buildAtom,
  buildPropertyPreAtom,
  getAtomResultType,
  isTransformerId,
  TransformerIdsEnum,
} from './expressionInferHelpers';
import {getEnumLabel, getMethodLabel, getPropertyDescription} from './expressionLabels';
import {Expression} from './expressionModel';
import {ExpressionsContext} from './expressionsContext';
import {ExpressionDependencies} from './expressionState';
import {isCompareMethod} from './infer/comparatorMethod';
import {InferredType, ResultType, ResultTypesEnum} from './infer/expressionResultModel';
import {Method, MethodCategoriesEnum} from './infer/method';

/*
 * Types.
 */

export interface AtomItem {
  id: string;
  name: string;
  tooltip?: string;
  atom: ClientAtom;
}

export type AtomItemGroups = ReadonlyArray<{
  id: MethodCategoriesEnum;
  items: ReadonlyArray<AtomItem>;
}>;

/*
 * Helpers.
 */

const resultTypeToCustomFieldPage: Partial<Record<ResultTypesEnum, SettingsCustomFieldsPagesEnum>> = {
  [ResultTypesEnum.CONTACT]: SettingsCustomFieldsPagesEnum.CARDS,
  [ResultTypesEnum.TEAMMATE]: SettingsCustomFieldsPagesEnum.TEAMMATES,
  [ResultTypesEnum.INBOX]: SettingsCustomFieldsPagesEnum.INBOXES,
  [ResultTypesEnum.ACCOUNT]: SettingsCustomFieldsPagesEnum.ACCOUNTS,
};

export function getCustomFieldFromResultType(resultType: ResultType, context: ExpressionsContext) {
  const page = resultTypeToCustomFieldPage[resultType.name];

  return page ? context.customFields[page] : [];
}

export function useListAtomGroups(
  atom: ClientAtom | undefined,
  context: ExpressionsContext,
  dependencies: ExpressionDependencies,
): AtomItemGroups {
  const featureFlags = useExpressionsFeatureFlags();

  return useMemo(() => {
    if (!atom) {
      return [];
    }

    return collectEnumValues(MethodCategoriesEnum)
      .map((category) => {
        const items = buildAtomItems(category, atom, context, featureFlags, dependencies);

        return {
          id: category,
          items,
        };
      })
      .filter((group) => group.items.length > 0);
  }, [atom, context, featureFlags, dependencies]);
}

function buildAtomItems(
  category: MethodCategoriesEnum,
  atom: ClientAtom,
  context: ExpressionsContext,
  featureFlags: FeatureFlagsMap,
  dependencies: ExpressionDependencies,
): ReadonlyArray<AtomItem> {
  const isInFunction = isInFunctionAtom(atom, context.expressions);

  const previousInfer = atom.inferredType;
  const resultType = getAtomResultType(atom);

  const transforms = Object.entries(resultType.methods || {})
    .filter(([, transformer]) => hasTransformerFeatureFlag(transformer, featureFlags))
    .filter(([, transformer]) => transformer && transformer.category === category)
    .filter(([, transformer]) => {
      if (!isInFunction) {
        return true;
      }

      // Avoid to create nested map.
      return transformer && !transformer.hasFunction;
    })
    .map(([transform]) => transform)
    .filter(isTransformerId);

  const preAtomItems: ReadonlyArray<AtomItem> = transforms.map((transform) => ({
    id: `transform-${transform}`,
    atom: buildAtom({transform}, previousInfer, context, dependencies),
    ...getMethodLabel(transform),
  }));

  switch (category) {
    case MethodCategoriesEnum.PROPERTY:
      return [...buildPropertyAtoms(resultType, previousInfer, context, dependencies), ...preAtomItems];

    case MethodCategoriesEnum.COMPARE:
      return buildCompareAtoms(resultType, previousInfer, context, dependencies);

    case MethodCategoriesEnum.CUSTOM_FIELD:
      return buildCustomFieldAtoms(resultType, previousInfer, context, dependencies);

    default:
      return preAtomItems;
  }
}

function buildPropertyAtoms(
  atomType: ResultType,
  previousInfer: InferredType,
  context: ExpressionsContext,
  dependencies: ExpressionDependencies,
): ReadonlyArray<AtomItem> {
  if (!atomType.properties) {
    return [];
  }

  return Object.keys(atomType.properties).map((property) => ({
    id: `property-${property}`,
    atom: buildAtom(buildPropertyPreAtom(property), previousInfer, context, dependencies),
    ...getPropertyDescription(property, atomType.name),
  }));
}

function buildCompareAtoms(
  resultType: ResultType,
  previousInfer: InferredType,
  context: ExpressionsContext,
  dependencies: ExpressionDependencies,
): ReadonlyArray<AtomItem> {
  const compare = resultType.methods[TransformerIdsEnum.COMPARE];
  if (!isCompareMethod(compare)) {
    return [];
  }

  return collectEnumValues(compare.comparatorEnum).map((operation) => ({
    id: `compare-${operation}`,
    atom: buildAtom(
      {transform: TransformerIdsEnum.COMPARE, params: {operation}},
      previousInfer,
      context,
      dependencies,
    ),
    ...getEnumLabel(operation),
  }));
}

function buildCustomFieldAtoms(
  resultType: ResultType,
  previousInfer: InferredType,
  context: ExpressionsContext,
  dependencies: ExpressionDependencies,
): ReadonlyArray<AtomItem> {
  if (!resultType.methods[TransformerIdsEnum.GET_CUSTOM_FIELD]) {
    return [];
  }

  return getCustomFieldFromResultType(resultType, context).map((customField) => ({
    id: `${resultType.name}-custom_field-${customField.id}`,
    name: customField.name,
    atom: buildAtom(
      {
        transform: TransformerIdsEnum.GET_CUSTOM_FIELD,
        params: {
          custom_field: customField.id,
        },
      },
      previousInfer,
      context,
      dependencies,
    ),
  }));
}

export function isAtomValidWhenItsReferencing(atom: ClientAtom, expressions: ReadonlyArray<Expression>) {
  const isUsePreviousAtom = atom.transform === TransformerIdsEnum.USE_PREVIOUS;
  // It's not referencing atom
  if (!isUsePreviousAtom) {
    return true;
  }

  // Reference is not linked yet
  if (!atom.params.expression) {
    return true;
  }

  return expressions.some((exp) => exp.id === atom.params.expression);
}

export function hasTransformerFeatureFlag(transformer: Method, featureFlags: FeatureFlagsMap): boolean {
  const {featureFlag} = transformer;

  return isUndefined(featureFlag) || Boolean(featureFlags[featureFlag]);
}
